import {
  OpenPencilFigmaSourceBundleSchema,
  OpenPencilBoundedVisualJudgementSchema,
  OpenPencilRuntimeProofResultSchema,
  OpenPencilSourceRightsDeclarationSchema,
  OPENPENCIL_VISUAL_JUDGEMENT_SCHEMA,
  assertOpenPencilSourceRightsDeclarationIntegrity,
  normalizeOpenPencilFigmaSourceBundle,
  type OpenPencilFigmaSourceBundle,
  type OpenPencilBoundedVisualJudgement,
  type OpenPencilRuntimeProofResult,
  type OpenPencilRuntimeScenarioProof,
  type OpenPencilSourceRightsDeclaration,
  type OpenPencilVisualAnnotation,
} from "./openpencil-figma-source";
import { z } from "zod";
import {
  assertDesignMutationExecutable,
  type DesignMutation,
  type FigmaDesignSourceReference,
} from "@cogo/design-data/design-mutation";
import {
  assertFigmaCompanionPatch,
  type FigmaCompanionPatch,
} from "@cogo/design-data/figma-companion";
import {
  OPENPENCIL_REVISION_VISUAL_DIFF_RUNNER,
  OPENPENCIL_REVISION_VISUAL_DIFF_SCHEMA,
  type OpenPencilRevisionVisualDiff,
} from "./openpencil-revision-visual-diff";

export const OPENPENCIL_SOURCE_WORKSPACE_SCHEMA =
  "cogo.studio.openpencil-source-workspace.v1" as const;

export type SourceDecisionStatus = "pending" | "accepted" | "rejected";
export type SourceDecisionActor = "human" | "agent";
export type SourceSurfaceRole =
  | "screen"
  | "reference"
  | "annotation"
  | "cover"
  | "history";

export interface SourceDecisionAudit {
  actor: SourceDecisionActor;
  updatedAt: string;
  reason?: string;
}

export interface SourcePageDecision extends SourceDecisionAudit {
  pageId: string;
  status: SourceDecisionStatus;
  storyId?: string;
  storyTitle?: string;
}

export interface SourceSectionDecision extends SourceDecisionAudit {
  sectionId: string;
  status: SourceDecisionStatus;
  storyId?: string;
  storyTitle?: string;
}

export interface SourceSurfaceDecision extends SourceDecisionAudit {
  surfaceId: string;
  status: SourceDecisionStatus;
  role: SourceSurfaceRole;
  targetPageId?: string;
  storyId?: string;
}

export interface SourceTokenDecision extends SourceDecisionAudit {
  tokenId: string;
  status: SourceDecisionStatus;
  canonicalToken?: string;
}

export type SourceVisualJudge = "gemma" | "human";
export type SourceVisualJudgementStatus = "approved" | "rejected" | "blocked";
export type SourceVisualAnnotation = OpenPencilVisualAnnotation;
export type SourceVisualJudgement = OpenPencilBoundedVisualJudgement;

export interface SourceAssemblyStoryMap {
  storyId: string;
  pageId: string;
  surfaceIds: string[];
}

export interface OpenPencilSourceAssemblyManifest {
  schemaVersion: "cogo.studio.openpencil-source-assembly-manifest.v1";
  manifestId: string;
  sourceId: string;
  sourceRevision: number;
  status: "validated" | "blocked";
  selectedPageIds: string[];
  selectedSectionIds: string[];
  selectedSurfaceIds: string[];
  selectedTokenIds: string[];
  storyMap: SourceAssemblyStoryMap[];
  visualJudgementIds: string[];
  blockers: string[];
  evidenceRefs: string[];
  createdAt: string;
  mayWriteApplicationJson: false;
}

export type SourceRuntimeScenarioProof = OpenPencilRuntimeScenarioProof;
export type OpenPencilSourceRuntimeProof = OpenPencilRuntimeProofResult;

export interface OpenPencilSourceCandidateBinding {
  schemaVersion: "cogo.studio.openpencil-source-candidate-binding.v1";
  candidateId: string;
  sourceId: string;
  sourceRevision: number;
  manifestId: string;
  inputSha256: string;
  applicationArtifactRef: string;
  previewUrl: string;
  authorityMode: "client-validated-candidate";
  createdAt: string;
}

export interface OpenPencilSourceWorkspace {
  schemaVersion: typeof OPENPENCIL_SOURCE_WORKSPACE_SCHEMA;
  sourceId: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
  bundle: OpenPencilFigmaSourceBundle;
  pageDecisions: Record<string, SourcePageDecision>;
  sectionDecisions: Record<string, SourceSectionDecision>;
  surfaceDecisions: Record<string, SourceSurfaceDecision>;
  tokenDecisions: Record<string, SourceTokenDecision>;
  visualJudgements: Record<string, SourceVisualJudgement>;
  revisionVisualDiff: OpenPencilRevisionVisualDiff | null;
  assemblyManifest: OpenPencilSourceAssemblyManifest | null;
  candidateBinding: OpenPencilSourceCandidateBinding | null;
  runtimeProof: OpenPencilSourceRuntimeProof | null;
  sourceRightsDeclaration: OpenPencilSourceRightsDeclaration | null;
  figmaCompanion: OpenPencilFigmaCompanionState;
}

export interface OpenPencilFigmaCompanionMutationReadback {
  schemaVersion: "cogo.studio.design-mutation-readback.v1";
  mutationId: string;
  mutationHash: string;
  cogoRevision: number;
  source: {
    sourceId: string;
    sourceRevision: number;
    sha256: string;
    artifactRef: string;
  };
  changedPaths: string[];
  changedSurfaceIds: string[];
  visualDiffStatus: string;
  appliedAt: string;
}

export interface OpenPencilFigmaCompanionSyncBackEvidence {
  sourceId: string;
  sourceRevision: number;
  sourceSha256: string;
  fileKey: string;
  upstreamVersion: string;
  syncedAt: string;
  receiptHash: string;
  authorityMode: "plugin-self-attested-selected-node-receipt";
}

export interface OpenPencilFigmaCompanionState {
  upstreamFigma: FigmaDesignSourceReference | null;
  pendingMutation: {
    mutation: DesignMutation;
    readback: OpenPencilFigmaCompanionMutationReadback;
  } | null;
  preparedPatch: FigmaCompanionPatch | null;
  syncBackEvidence: OpenPencilFigmaCompanionSyncBackEvidence | null;
}

export interface OpenPencilSourceReleaseAdmission {
  status: "blocked" | "admitted";
  reasonCodes: string[];
  requiredGateIds: string[];
  evaluatedAt: string;
}

const decisionStatus = z.enum(["pending", "accepted", "rejected"]);
const actor = z.enum(["human", "agent"]);
const audit = {
  actor,
  updatedAt: z.string().min(1),
  reason: z.string().optional(),
};

const visualJudgement = OpenPencilBoundedVisualJudgementSchema.extend({
  schemaVersion: z
    .literal(OPENPENCIL_VISUAL_JUDGEMENT_SCHEMA)
    .default(OPENPENCIL_VISUAL_JUDGEMENT_SCHEMA),
});

const assemblyManifest = z.object({
  schemaVersion: z.literal(
    "cogo.studio.openpencil-source-assembly-manifest.v1",
  ),
  manifestId: z.string().min(1),
  sourceId: z.string().min(1),
  sourceRevision: z.number().int().nonnegative(),
  status: z.enum(["validated", "blocked"]),
  selectedPageIds: z.array(z.string().min(1)),
  selectedSectionIds: z.array(z.string().min(1)),
  selectedSurfaceIds: z.array(z.string().min(1)),
  selectedTokenIds: z.array(z.string().min(1)),
  storyMap: z.array(
    z.object({
      storyId: z.string().min(1),
      pageId: z.string().min(1),
      surfaceIds: z.array(z.string().min(1)).min(1),
    }),
  ),
  visualJudgementIds: z.array(z.string().min(1)),
  blockers: z.array(z.string().min(1)),
  evidenceRefs: z.array(z.string().min(1)),
  createdAt: z.string().min(1),
  mayWriteApplicationJson: z.literal(false),
});

const runtimeProof = OpenPencilRuntimeProofResultSchema;
const candidateBinding = z.object({
  schemaVersion: z.literal(
    "cogo.studio.openpencil-source-candidate-binding.v1",
  ),
  candidateId: z.string().min(1),
  sourceId: z.string().min(1),
  sourceRevision: z.number().int().nonnegative(),
  manifestId: z.string().min(1),
  inputSha256: z.string().regex(/^[a-f0-9]{64}$/),
  applicationArtifactRef: z.string().min(1),
  previewUrl: z.string().url(),
  authorityMode: z.literal("client-validated-candidate"),
  createdAt: z.string().min(1),
});

const revisionSurfaceDiff = z.object({
  surfaceId: z.string().min(1),
  status: z.enum(["unchanged", "changed", "blocked"]),
  beforeArtifactRef: z.string().min(1).nullable(),
  afterArtifactRef: z.string().min(1).nullable(),
  beforeSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  afterSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  changedPixels: z.number().int().nonnegative().nullable(),
  totalPixels: z.number().int().nonnegative().nullable(),
  diffRatio: z.number().min(0).max(1).nullable(),
  blocker: z.string().min(1).optional(),
});

const revisionVisualDiff = z.object({
  schemaVersion: z.literal(OPENPENCIL_REVISION_VISUAL_DIFF_SCHEMA),
  runner: z.literal(OPENPENCIL_REVISION_VISUAL_DIFF_RUNNER),
  parentSourceId: z.string().min(1),
  sourceId: z.string().min(1),
  sourceRevision: z.number().int().positive(),
  patchId: z.string().min(1),
  status: z.enum(["approved", "pending", "blocked"]),
  disposition: z.enum([
    "no-change",
    "pending",
    "human-approved",
    "gemma-approved",
  ]),
  changedSurfaceIds: z.array(z.string().min(1)),
  surfaces: z.array(revisionSurfaceDiff),
  evidenceRefs: z.array(z.string().min(1)),
  blockers: z.array(z.string().min(1)),
  pixelThreshold: z.number().nonnegative(),
  evaluatedAt: z.string().min(1),
  judgementId: z.string().min(1).optional(),
});

const figmaDesignSourceReference = z.object({
  kind: z.literal("figma-reference"),
  fileKey: z.string().min(1),
  nodeId: z.string().min(1),
  url: z.string().url(),
  observedAt: z.string().datetime(),
  upstreamVersion: z.string().min(1).optional(),
  upstreamHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
});

const figmaCompanionMutationReadback = z.object({
  schemaVersion: z.literal("cogo.studio.design-mutation-readback.v1"),
  mutationId: z.string().min(1),
  mutationHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  cogoRevision: z.number().int().nonnegative(),
  source: z.object({
    sourceId: z.string().min(1),
    sourceRevision: z.number().int().positive(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    artifactRef: z.string().min(1),
  }),
  changedPaths: z.array(z.string().min(1)),
  changedSurfaceIds: z.array(z.string().min(1)),
  visualDiffStatus: z.string().min(1),
  appliedAt: z.string().datetime(),
});

const persistedDesignMutation = z.custom<DesignMutation>((value) => {
  try {
    assertDesignMutationExecutable(value);
    return true;
  } catch {
    return false;
  }
});

const persistedFigmaCompanionPatch = z.custom<FigmaCompanionPatch>((value) => {
  try {
    assertFigmaCompanionPatch(value);
    return true;
  } catch {
    return false;
  }
});

const figmaCompanionSyncBackEvidence = z.object({
  sourceId: z.string().min(1),
  sourceRevision: z.number().int().positive(),
  sourceSha256: z.string().regex(/^[a-f0-9]{64}$/),
  fileKey: z.string().min(1),
  upstreamVersion: z.string().min(1),
  syncedAt: z.string().datetime(),
  receiptHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  authorityMode: z.literal("plugin-self-attested-selected-node-receipt"),
});

const figmaCompanionState = z.object({
  upstreamFigma: figmaDesignSourceReference.nullable().default(null),
  pendingMutation: z
    .object({
      mutation: persistedDesignMutation,
      readback: figmaCompanionMutationReadback,
    })
    .nullable()
    .default(null),
  preparedPatch: persistedFigmaCompanionPatch.nullable().default(null),
  syncBackEvidence: figmaCompanionSyncBackEvidence.nullable().default(null),
});

export const OpenPencilSourceWorkspaceSchema = z.object({
  schemaVersion: z.literal(OPENPENCIL_SOURCE_WORKSPACE_SCHEMA),
  sourceId: z.string().min(1),
  revision: z.number().int().nonnegative(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  bundle: OpenPencilFigmaSourceBundleSchema,
  pageDecisions: z.record(
    z.object({
      pageId: z.string().min(1),
      status: decisionStatus,
      storyId: z.string().optional(),
      storyTitle: z.string().optional(),
      ...audit,
    }),
  ),
  sectionDecisions: z.record(
    z.object({
      sectionId: z.string().min(1),
      status: decisionStatus,
      storyId: z.string().optional(),
      storyTitle: z.string().optional(),
      ...audit,
    }),
  ),
  surfaceDecisions: z.record(
    z.object({
      surfaceId: z.string().min(1),
      status: decisionStatus,
      role: z.enum(["screen", "reference", "annotation", "cover", "history"]),
      targetPageId: z.string().optional(),
      storyId: z.string().optional(),
      ...audit,
    }),
  ),
  tokenDecisions: z.record(
    z.object({
      tokenId: z.string().min(1),
      status: decisionStatus,
      canonicalToken: z.string().optional(),
      ...audit,
    }),
  ),
  visualJudgements: z.record(visualJudgement).default({}),
  revisionVisualDiff: revisionVisualDiff.nullable().default(null),
  assemblyManifest: assemblyManifest.nullable().default(null),
  candidateBinding: candidateBinding.nullable().default(null),
  runtimeProof: runtimeProof.nullable().default(null),
  sourceRightsDeclaration:
    OpenPencilSourceRightsDeclarationSchema.nullable().default(null),
  figmaCompanion: figmaCompanionState.default({
    upstreamFigma: null,
    pendingMutation: null,
    preparedPatch: null,
    syncBackEvidence: null,
  }),
});

function timestamp(value?: string): string {
  return value ?? new Date().toISOString();
}

function surfaceRole(
  name: string,
  classification: "mobile" | "non-mobile",
): SourceSurfaceRole {
  const normalized = name.toLowerCase();
  if (normalized.includes("cover")) return "cover";
  if (normalized.includes("이력") || normalized.includes("history")) {
    return "history";
  }
  return classification === "mobile" ? "screen" : "reference";
}

function sameIds(left: string[], right: string[]): boolean {
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return (
    sortedLeft.length === sortedRight.length &&
    sortedLeft.every((value, index) => value === sortedRight[index])
  );
}

function acceptedScreenSurfaceIds(
  workspace: OpenPencilSourceWorkspace,
): string[] {
  return Object.values(workspace.surfaceDecisions)
    .filter(
      (decision) =>
        decision.status === "accepted" && decision.role === "screen",
    )
    .map((decision) => decision.surfaceId)
    .sort();
}

function approvedVisualJudgements(
  workspace: OpenPencilSourceWorkspace,
): SourceVisualJudgement[] {
  const previewRefs = new Map(
    (workspace.bundle.visualPreviewManifest?.items ?? []).map((item) => [
      item.surfaceId,
      item.artifactRef,
    ]),
  );
  return Object.values(workspace.visualJudgements).filter(
    (judgement) =>
      judgement.status === "approved" &&
      !judgement.annotations.some(
        (annotation) => annotation.severity === "blocking",
      ) &&
      (judgement.judge === "human" || judgement.confidence >= 0.85) &&
      judgement.surfaceIds.every((surfaceId) => {
        const previewRef = previewRefs.get(surfaceId);
        return Boolean(
          previewRef && judgement.evidenceRefs.includes(previewRef),
        );
      }),
  );
}

function visualPreviewComplete(workspace: OpenPencilSourceWorkspace): boolean {
  const accepted = acceptedScreenSurfaceIds(workspace);
  if (accepted.length === 0) return false;
  const previews = new Set(
    (workspace.bundle.visualPreviewManifest?.items ?? []).map(
      (item) => item.surfaceId,
    ),
  );
  return accepted.every((surfaceId) => previews.has(surfaceId));
}

function visualJudgementComplete(
  workspace: OpenPencilSourceWorkspace,
): boolean {
  const surfaceIds = acceptedScreenSurfaceIds(workspace);
  if (surfaceIds.length === 0) return false;
  const covered = new Set(
    approvedVisualJudgements(workspace).flatMap(
      (judgement) => judgement.surfaceIds,
    ),
  );
  return surfaceIds.every((surfaceId) => covered.has(surfaceId));
}

function assemblyComplete(workspace: OpenPencilSourceWorkspace): boolean {
  const manifest = workspace.assemblyManifest;
  if (
    !manifest ||
    manifest.status !== "validated" ||
    manifest.blockers.length
  ) {
    return false;
  }
  const acceptedPages = Object.values(workspace.pageDecisions)
    .filter((decision) => decision.status === "accepted")
    .map((decision) => decision.pageId);
  const acceptedSections = Object.values(workspace.sectionDecisions)
    .filter((decision) => decision.status === "accepted")
    .map((decision) => decision.sectionId);
  const acceptedSurfaces = acceptedScreenSurfaceIds(workspace);
  const acceptedTokens = Object.values(workspace.tokenDecisions)
    .filter((decision) => decision.status === "accepted")
    .map((decision) => decision.tokenId);
  const mappedSurfaces = manifest.storyMap.flatMap((story) => story.surfaceIds);
  return (
    manifest.sourceId === workspace.sourceId &&
    manifest.mayWriteApplicationJson === false &&
    sameIds(manifest.selectedPageIds, acceptedPages) &&
    sameIds(manifest.selectedSectionIds, acceptedSections) &&
    sameIds(manifest.selectedSurfaceIds, acceptedSurfaces) &&
    sameIds(manifest.selectedTokenIds, acceptedTokens) &&
    sameIds(mappedSurfaces, acceptedSurfaces) &&
    manifest.visualJudgementIds.every((judgementId) =>
      Boolean(workspace.visualJudgements[judgementId]),
    )
  );
}

function runtimeProofComplete(workspace: OpenPencilSourceWorkspace): boolean {
  const proof = workspace.runtimeProof;
  const manifest = workspace.assemblyManifest;
  const candidate = workspace.candidateBinding;
  if (
    !proof ||
    !manifest ||
    !candidate ||
    proof.manifestId !== manifest.manifestId ||
    proof.manifestId !== candidate.manifestId ||
    proof.previewUrl !== candidate.previewUrl ||
    proof.applicationArtifactRef !== candidate.applicationArtifactRef
  )
    return false;
  if (
    proof.status !== "passed" ||
    proof.blockers.length > 0 ||
    !proof.scenarios.every((scenario) => scenario.status === "passed")
  ) {
    return false;
  }
  return true;
}

function withProgressAdmission(
  workspace: Omit<OpenPencilSourceWorkspace, "bundle"> & {
    bundle: OpenPencilFigmaSourceBundle;
  },
): OpenPencilFigmaSourceBundle {
  const mappingDecisions = [
    ...Object.values(workspace.pageDecisions),
    ...Object.values(workspace.sectionDecisions),
    ...Object.values(workspace.surfaceDecisions),
  ];
  const mappingComplete =
    mappingDecisions.length > 0 &&
    mappingDecisions.every((decision) => decision.status !== "pending") &&
    Object.values(workspace.surfaceDecisions).some(
      (decision) =>
        decision.status === "accepted" && decision.role === "screen",
    );
  const tokenDecisions = Object.values(workspace.tokenDecisions);
  const tokensComplete =
    (tokenDecisions.length > 0 ||
      (workspace.bundle.sourceFacts?.designTokens.length ?? 0) === 0) &&
    tokenDecisions.every((decision) => decision.status !== "pending");
  const previewsComplete = visualPreviewComplete(workspace);
  const visualsComplete = visualJudgementComplete(workspace);
  const revisionVisualsComplete =
    !workspace.bundle.revisionLineage ||
    workspace.revisionVisualDiff?.status === "approved";
  const selectedAssemblyComplete = assemblyComplete(workspace);
  const reasonCodes = [
    ...(!mappingComplete ? ["source_mapping_pending"] : []),
    ...(!tokensComplete ? ["token_decisions_pending"] : []),
    ...(!previewsComplete ? ["visual_preview_pending"] : []),
    ...(!visualsComplete ? ["visual_judgement_pending"] : []),
    ...(!revisionVisualsComplete ? ["revision_visual_diff_pending"] : []),
    ...(!selectedAssemblyComplete ? ["application_assembly_pending"] : []),
  ];
  return {
    ...workspace.bundle,
    generationAdmission: {
      status: reasonCodes.length === 0 ? "admitted" : "blocked",
      reasonCodes,
      requiredGateIds: [
        ...(!mappingComplete ? ["source-mapping-approved"] : []),
        ...(!tokensComplete ? ["design-token-decisions-approved"] : []),
        ...(!previewsComplete ? ["surface-preview-evidence"] : []),
        ...(!visualsComplete ? ["visual-semantics-approved"] : []),
        ...(!revisionVisualsComplete ? ["revision-visual-diff-approved"] : []),
        ...(!selectedAssemblyComplete
          ? ["application-assembly-validated"]
          : []),
      ],
      evaluatedAt: workspace.updatedAt,
    },
  };
}

export function evaluateOpenPencilSourceReleaseAdmission(
  workspace: OpenPencilSourceWorkspace | null | undefined,
): OpenPencilSourceReleaseAdmission {
  if (!workspace) {
    return {
      status: "admitted",
      reasonCodes: [],
      requiredGateIds: [],
      evaluatedAt: new Date(0).toISOString(),
    };
  }
  const generationReady =
    workspace.bundle.generationAdmission.status === "admitted";
  const candidateReady = Boolean(workspace.candidateBinding);
  const runtimeReady = runtimeProofComplete(workspace);
  const reasonCodes = [
    ...(!generationReady ? ["generation_admission_pending"] : []),
    ...(!candidateReady ? ["candidate_binding_pending"] : []),
    ...(!runtimeReady ? ["runtime_proof_pending"] : []),
  ];
  return {
    status: reasonCodes.length === 0 ? "admitted" : "blocked",
    reasonCodes,
    requiredGateIds: [
      ...(!generationReady ? ["cogo-json-generation-admission"] : []),
      ...(!candidateReady ? ["source-candidate-bound"] : []),
      ...(!runtimeReady ? ["runtime-proof-passed"] : []),
    ],
    evaluatedAt: workspace.updatedAt,
  };
}

export function assertOpenPencilSourceReleaseAdmitted(
  workspace: OpenPencilSourceWorkspace | null | undefined,
): void {
  const result = evaluateOpenPencilSourceReleaseAdmission(workspace);
  if (result.status !== "admitted") {
    throw new Error(
      `Source-derived application is not ready for export, publish, or deploy. Required gates: ${result.requiredGateIds.join(", ")}`,
    );
  }
}

export function createOpenPencilSourceWorkspace(
  bundleInput: OpenPencilFigmaSourceBundle,
  generatedAt?: string,
): OpenPencilSourceWorkspace {
  const bundle = normalizeOpenPencilFigmaSourceBundle(bundleInput);
  const now = timestamp(generatedAt);
  const sourceFacts = bundle.sourceFacts;
  const base: OpenPencilSourceWorkspace = {
    schemaVersion: OPENPENCIL_SOURCE_WORKSPACE_SCHEMA,
    sourceId: bundle.sourceId,
    revision: 0,
    createdAt: now,
    updatedAt: now,
    bundle,
    pageDecisions: Object.fromEntries(
      (sourceFacts?.pages ?? []).map((page) => [
        page.id,
        {
          pageId: page.id,
          status: "pending",
          actor: "human",
          updatedAt: now,
        } satisfies SourcePageDecision,
      ]),
    ),
    sectionDecisions: Object.fromEntries(
      (sourceFacts?.storySections ?? []).map((section) => [
        section.id,
        {
          sectionId: section.id,
          status: "pending",
          actor: "human",
          updatedAt: now,
        } satisfies SourceSectionDecision,
      ]),
    ),
    surfaceDecisions: Object.fromEntries(
      (sourceFacts?.surfaceCandidates ?? []).map((surface) => [
        surface.id,
        {
          surfaceId: surface.id,
          status: "pending",
          role: surfaceRole(surface.name, surface.classification),
          targetPageId: surface.pageId,
          actor: "human",
          updatedAt: now,
        } satisfies SourceSurfaceDecision,
      ]),
    ),
    tokenDecisions: Object.fromEntries(
      (sourceFacts?.designTokens ?? []).map((token) => [
        token.id,
        {
          tokenId: token.id,
          status: "pending",
          actor: "human",
          updatedAt: now,
        } satisfies SourceTokenDecision,
      ]),
    ),
    visualJudgements: {},
    revisionVisualDiff: null,
    assemblyManifest: null,
    candidateBinding: null,
    runtimeProof: null,
    sourceRightsDeclaration: null,
    figmaCompanion: {
      upstreamFigma: null,
      pendingMutation: null,
      preparedPatch: null,
      syncBackEvidence: null,
    },
  };
  return OpenPencilSourceWorkspaceSchema.parse({
    ...base,
    bundle: withProgressAdmission(base),
  });
}

/**
 * Carries stable-ID mapping and token decisions into a coordinated source+Cogo
 * revision. Visual judgements and release proofs remain revision-scoped and
 * are intentionally reset for fresh evidence.
 */
export function carryOpenPencilSourceDecisionsToRevision(
  previous: OpenPencilSourceWorkspace,
  nextBundle: OpenPencilFigmaSourceBundle,
  updatedAt?: string,
): OpenPencilSourceWorkspace {
  const lineage = nextBundle.revisionLineage;
  if (
    !lineage ||
    lineage.parentSourceId !== previous.bundle.sourceId ||
    lineage.parentSha256 !== previous.bundle.fileSha256 ||
    lineage.sourceRevision !==
      (previous.bundle.revisionLineage?.sourceRevision ?? 0) + 1
  ) {
    throw new Error("OpenPencil revision lineage is missing or inconsistent.");
  }
  const next = createOpenPencilSourceWorkspace(nextBundle, updatedAt);
  const carry = <T>(
    current: Record<string, T>,
    prior: Record<string, T>,
  ): Record<string, T> =>
    Object.fromEntries(
      Object.keys(current).map((id) => [
        id,
        structuredClone(prior[id] ?? current[id]),
      ]),
    );
  next.pageDecisions = carry(next.pageDecisions, previous.pageDecisions);
  next.sectionDecisions = carry(
    next.sectionDecisions,
    previous.sectionDecisions,
  );
  next.surfaceDecisions = carry(
    next.surfaceDecisions,
    previous.surfaceDecisions,
  );
  next.tokenDecisions = carry(next.tokenDecisions, previous.tokenDecisions);
  next.revision = previous.revision;
  next.createdAt = previous.createdAt;
  next.updatedAt = timestamp(updatedAt);
  next.visualJudgements = {};
  next.revisionVisualDiff = null;
  next.assemblyManifest = null;
  next.candidateBinding = null;
  next.runtimeProof = null;
  next.sourceRightsDeclaration = null;
  next.figmaCompanion = structuredClone(previous.figmaCompanion);
  next.figmaCompanion.preparedPatch = null;
  next.figmaCompanion.syncBackEvidence = null;
  const normalized = normalizeOpenPencilSourceWorkspace(next);
  if (!normalized) {
    throw new Error("Could not carry source decisions to the new revision.");
  }
  return normalized;
}

export function normalizeOpenPencilSourceWorkspace(
  value: unknown,
): OpenPencilSourceWorkspace | null {
  const parsed = OpenPencilSourceWorkspaceSchema.safeParse(value);
  if (!parsed.success) return null;
  const workspace = parsed.data as OpenPencilSourceWorkspace;
  if (
    workspace.sourceRightsDeclaration &&
    !sourceRightsDeclarationMatchesWorkspace(
      workspace.sourceRightsDeclaration,
      workspace,
    )
  ) {
    workspace.sourceRightsDeclaration = null;
  }
  return OpenPencilSourceWorkspaceSchema.parse({
    ...workspace,
    bundle: withProgressAdmission(workspace),
  });
}

export async function normalizePersistedOpenPencilSourceWorkspace(
  value: unknown,
  expectedScope?: {
    projectId?: string | null;
    tenantId?: string | null;
    principalId?: string | null;
  },
): Promise<OpenPencilSourceWorkspace | null> {
  const workspace = normalizeOpenPencilSourceWorkspace(value);
  const declaration = workspace?.sourceRightsDeclaration;
  if (!workspace || !declaration) return workspace;
  try {
    await assertOpenPencilSourceRightsDeclarationIntegrity(declaration);
    if (
      expectedScope?.projectId &&
      declaration.scope.projectId !== expectedScope.projectId
    ) {
      throw new Error("OPENPENCIL_SOURCE_RIGHTS_DECLARATION_SCOPE_MISMATCH");
    }
    if (
      (expectedScope?.tenantId &&
        declaration.scope.tenantId !== expectedScope.tenantId) ||
      (expectedScope?.principalId &&
        declaration.scope.principalId !== expectedScope.principalId)
    ) {
      throw new Error("OPENPENCIL_SOURCE_RIGHTS_DECLARATION_ACTOR_MISMATCH");
    }
    return workspace;
  } catch {
    return OpenPencilSourceWorkspaceSchema.parse({
      ...workspace,
      sourceRightsDeclaration: null,
    });
  }
}

export async function recordOpenPencilSourceRightsDeclaration(
  workspace: OpenPencilSourceWorkspace,
  value: OpenPencilSourceRightsDeclaration,
): Promise<OpenPencilSourceWorkspace> {
  const declaration =
    await assertOpenPencilSourceRightsDeclarationIntegrity(value);
  if (!sourceRightsDeclarationMatchesWorkspace(declaration, workspace)) {
    throw new Error(
      "OPENPENCIL_SOURCE_RIGHTS_DECLARATION_WORKSPACE_MISMATCH",
    );
  }
  return updateWorkspace(
    workspace,
    declaration.declaredAt,
    (draft) => {
      draft.sourceRightsDeclaration = structuredClone(declaration);
    },
    false,
    false,
  );
}

function sourceRightsDeclarationMatchesWorkspace(
  declaration: OpenPencilSourceRightsDeclaration,
  workspace: OpenPencilSourceWorkspace,
): boolean {
  const expectedArtifactRef = workspace.bundle.sourceArtifactRef;
  return Boolean(
    workspace.bundle.fileSha256 &&
      declaration.source.sourceId === workspace.sourceId &&
      declaration.source.sourceRevision ===
        (workspace.bundle.revisionLineage?.sourceRevision ?? 0) &&
      declaration.source.fileSha256 === workspace.bundle.fileSha256 &&
      declaration.source.fileName === workspace.bundle.fileName &&
      (!expectedArtifactRef ||
        declaration.source.sourceArtifactRef === expectedArtifactRef),
  );
}

function updateWorkspace(
  workspace: OpenPencilSourceWorkspace,
  updatedAt: string,
  update: (draft: OpenPencilSourceWorkspace) => void,
  invalidateProofs = true,
  advanceRevision = true,
): OpenPencilSourceWorkspace {
  const draft = structuredClone(workspace);
  if (advanceRevision) {
    draft.revision += 1;
  }
  draft.updatedAt = updatedAt;
  if (invalidateProofs) {
    draft.assemblyManifest = null;
    draft.candidateBinding = null;
    draft.runtimeProof = null;
  }
  update(draft);
  draft.bundle = withProgressAdmission(draft);
  return OpenPencilSourceWorkspaceSchema.parse(draft);
}

export function bindOpenPencilFigmaCompanionUpstream(
  workspace: OpenPencilSourceWorkspace,
  upstreamFigma: FigmaDesignSourceReference,
  updatedAt?: string,
): OpenPencilSourceWorkspace {
  const parsed = figmaDesignSourceReference.parse(upstreamFigma);
  return updateWorkspace(
    workspace,
    timestamp(updatedAt),
    (draft) => {
      draft.figmaCompanion = {
        upstreamFigma: parsed,
        pendingMutation: null,
        preparedPatch: null,
        syncBackEvidence: null,
      };
    },
    false,
  );
}

export function recordOpenPencilFigmaCompanionMutation(
  workspace: OpenPencilSourceWorkspace,
  input: {
    mutation: DesignMutation;
    readback: OpenPencilFigmaCompanionMutationReadback;
    updatedAt?: string;
  },
): OpenPencilSourceWorkspace {
  assertDesignMutationExecutable(input.mutation);
  const readback = figmaCompanionMutationReadback.parse(input.readback);
  if (
    readback.mutationId !== input.mutation.mutationId ||
    readback.mutationHash !== input.mutation.contentHash ||
    readback.source.sourceId !== workspace.sourceId ||
    readback.source.sha256 !== workspace.bundle.fileSha256
  ) {
    throw new Error("FIGMA_COMPANION_PERSISTED_MUTATION_READBACK_MISMATCH");
  }
  return updateWorkspace(
    workspace,
    timestamp(input.updatedAt ?? readback.appliedAt),
    (draft) => {
      draft.figmaCompanion.pendingMutation = {
        mutation: structuredClone(input.mutation),
        readback: structuredClone(readback),
      };
      draft.figmaCompanion.preparedPatch = null;
      draft.figmaCompanion.syncBackEvidence = null;
    },
    false,
  );
}

export function recordOpenPencilFigmaCompanionPreparedPatch(
  workspace: OpenPencilSourceWorkspace,
  patch: FigmaCompanionPatch,
  updatedAt?: string,
): OpenPencilSourceWorkspace {
  assertFigmaCompanionPatch(patch);
  const pending = workspace.figmaCompanion.pendingMutation;
  if (
    !pending ||
    patch.semanticAuthority.kind !== "cogo-design-mutation" ||
    patch.semanticAuthority.ref !== pending.mutation.mutationId ||
    patch.semanticAuthority.contentHash !== pending.mutation.contentHash ||
    patch.source.openPencilSourceId !== pending.readback.source.sourceId ||
    patch.source.openPencilSourceRevision !==
      pending.readback.source.sourceRevision ||
    patch.source.openPencilSourceSha256 !== pending.readback.source.sha256
  ) {
    throw new Error("FIGMA_COMPANION_PREPARED_PATCH_AUTHORITY_MISMATCH");
  }
  return updateWorkspace(
    workspace,
    timestamp(updatedAt),
    (draft) => {
      draft.figmaCompanion.preparedPatch = structuredClone(patch);
    },
    false,
  );
}

export function recordOpenPencilFigmaCompanionSyncBackEvidence(
  workspace: OpenPencilSourceWorkspace,
  evidence: OpenPencilFigmaCompanionSyncBackEvidence,
): OpenPencilSourceWorkspace {
  const parsed = figmaCompanionSyncBackEvidence.parse(evidence);
  const patch = workspace.figmaCompanion.preparedPatch;
  if (
    !patch ||
    parsed.sourceId !== patch.source.openPencilSourceId ||
    parsed.sourceRevision !== patch.source.openPencilSourceRevision ||
    parsed.sourceSha256 !== patch.source.openPencilSourceSha256 ||
    parsed.fileKey !== patch.source.fileKey
  ) {
    throw new Error("FIGMA_COMPANION_SYNC_BACK_EVIDENCE_AUTHORITY_MISMATCH");
  }
  return updateWorkspace(
    workspace,
    parsed.syncedAt,
    (draft) => {
      draft.figmaCompanion.syncBackEvidence = structuredClone(parsed);
      draft.figmaCompanion.pendingMutation = null;
    },
    false,
  );
}

export function setSourcePageDecision(
  workspace: OpenPencilSourceWorkspace,
  input: Omit<SourcePageDecision, "updatedAt"> & { updatedAt?: string },
): OpenPencilSourceWorkspace {
  if (!workspace.pageDecisions[input.pageId]) {
    throw new Error(`Unknown source page: ${input.pageId}`);
  }
  const updatedAt = timestamp(input.updatedAt);
  return updateWorkspace(workspace, updatedAt, (draft) => {
    draft.pageDecisions[input.pageId] = { ...input, updatedAt };
  });
}

export function setSourceSectionDecision(
  workspace: OpenPencilSourceWorkspace,
  input: Omit<SourceSectionDecision, "updatedAt"> & { updatedAt?: string },
): OpenPencilSourceWorkspace {
  if (!workspace.sectionDecisions[input.sectionId]) {
    throw new Error(`Unknown source section: ${input.sectionId}`);
  }
  const updatedAt = timestamp(input.updatedAt);
  return updateWorkspace(workspace, updatedAt, (draft) => {
    draft.sectionDecisions[input.sectionId] = { ...input, updatedAt };
  });
}

export function setSourceSurfaceDecision(
  workspace: OpenPencilSourceWorkspace,
  input: Omit<SourceSurfaceDecision, "updatedAt"> & { updatedAt?: string },
): OpenPencilSourceWorkspace {
  if (!workspace.surfaceDecisions[input.surfaceId]) {
    throw new Error(`Unknown source surface: ${input.surfaceId}`);
  }
  const updatedAt = timestamp(input.updatedAt);
  return updateWorkspace(workspace, updatedAt, (draft) => {
    draft.surfaceDecisions[input.surfaceId] = { ...input, updatedAt };
  });
}

export function setSourceTokenDecision(
  workspace: OpenPencilSourceWorkspace,
  input: Omit<SourceTokenDecision, "updatedAt"> & { updatedAt?: string },
): OpenPencilSourceWorkspace {
  if (!workspace.tokenDecisions[input.tokenId]) {
    throw new Error(`Unknown source token: ${input.tokenId}`);
  }
  const updatedAt = timestamp(input.updatedAt);
  return updateWorkspace(workspace, updatedAt, (draft) => {
    draft.tokenDecisions[input.tokenId] = { ...input, updatedAt };
  });
}

export function setAllSourceTokenDecisions(
  workspace: OpenPencilSourceWorkspace,
  input: {
    status: SourceDecisionStatus;
    actor: SourceDecisionActor;
    reason?: string;
    updatedAt?: string;
  },
): OpenPencilSourceWorkspace {
  const updatedAt = timestamp(input.updatedAt);
  return updateWorkspace(workspace, updatedAt, (draft) => {
    for (const tokenId of Object.keys(draft.tokenDecisions)) {
      draft.tokenDecisions[tokenId] = {
        ...draft.tokenDecisions[tokenId],
        status: input.status,
        actor: input.actor,
        updatedAt,
        ...(input.reason ? { reason: input.reason } : {}),
      };
    }
  });
}

export function applySuggestedSourceMapping(
  workspace: OpenPencilSourceWorkspace,
  input: {
    actor: SourceDecisionActor;
    reason?: string;
    updatedAt?: string;
  },
): OpenPencilSourceWorkspace {
  const updatedAt = timestamp(input.updatedAt);
  const screenPageIds = new Set(
    Object.values(workspace.surfaceDecisions)
      .filter((decision) => decision.role === "screen")
      .map((decision) => decision.targetPageId)
      .filter((pageId): pageId is string => Boolean(pageId)),
  );
  const sectionPage = new Map(
    (workspace.bundle.sourceFacts?.storySections ?? []).map((section) => [
      section.id,
      section.pageId,
    ]),
  );
  return updateWorkspace(workspace, updatedAt, (draft) => {
    for (const [pageId, decision] of Object.entries(draft.pageDecisions)) {
      draft.pageDecisions[pageId] = {
        ...decision,
        status: screenPageIds.has(pageId) ? "accepted" : "rejected",
        actor: input.actor,
        updatedAt,
        ...(input.reason ? { reason: input.reason } : {}),
      };
    }
    for (const [sectionId, decision] of Object.entries(
      draft.sectionDecisions,
    )) {
      const accepted = screenPageIds.has(sectionPage.get(sectionId) ?? "");
      draft.sectionDecisions[sectionId] = {
        ...decision,
        status: accepted ? "accepted" : "rejected",
        actor: input.actor,
        updatedAt,
        ...(input.reason ? { reason: input.reason } : {}),
      };
    }
    for (const [surfaceId, decision] of Object.entries(
      draft.surfaceDecisions,
    )) {
      draft.surfaceDecisions[surfaceId] = {
        ...decision,
        status: decision.role === "screen" ? "accepted" : "rejected",
        actor: input.actor,
        updatedAt,
        ...(input.reason ? { reason: input.reason } : {}),
      };
    }
  });
}

function stableId(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function recordSourceRevisionVisualDiff(
  workspace: OpenPencilSourceWorkspace,
  input: OpenPencilRevisionVisualDiff,
): OpenPencilSourceWorkspace {
  const lineage = workspace.bundle.revisionLineage;
  if (
    !lineage ||
    input.sourceId !== workspace.sourceId ||
    input.parentSourceId !== lineage.parentSourceId ||
    input.sourceRevision !== lineage.sourceRevision ||
    input.patchId !== lineage.patchId
  ) {
    throw new Error(
      "Revision visual diff does not match the active source lineage.",
    );
  }
  const previewRefs = new Set(
    (workspace.bundle.visualPreviewManifest?.items ?? []).map(
      (item) => item.artifactRef,
    ),
  );
  if (
    input.surfaces.some(
      (surface) =>
        surface.afterArtifactRef !== null &&
        !previewRefs.has(surface.afterArtifactRef),
    )
  ) {
    throw new Error(
      "Revision visual diff contains an unverified current preview ref.",
    );
  }
  return updateWorkspace(workspace, input.evaluatedAt, (draft) => {
    draft.revisionVisualDiff = structuredClone(input);
  });
}

export function recordSourceVisualJudgement(
  workspace: OpenPencilSourceWorkspace,
  input: {
    judgementId?: string;
    judge: SourceVisualJudge;
    model?: string;
    status: SourceVisualJudgementStatus;
    confidence: number;
    surfaceIds: string[];
    annotations?: SourceVisualAnnotation[];
    evidenceRefs: string[];
    reason?: string;
    updatedAt?: string;
  },
): OpenPencilSourceWorkspace {
  const surfaceIds = [...new Set(input.surfaceIds)];
  if (surfaceIds.length === 0) {
    throw new Error("Visual judgement must target at least one surface.");
  }
  for (const surfaceId of surfaceIds) {
    const decision = workspace.surfaceDecisions[surfaceId];
    if (
      !decision ||
      decision.status !== "accepted" ||
      decision.role !== "screen"
    ) {
      throw new Error(
        `Visual judgement targets an unaccepted screen: ${surfaceId}`,
      );
    }
  }
  if (input.judge === "gemma" && !input.model) {
    throw new Error("Gemma visual judgement must record its model identifier.");
  }
  const annotations = input.annotations ?? [];
  if (
    input.status === "approved" &&
    annotations.some((annotation) => annotation.severity === "blocking")
  ) {
    throw new Error(
      "Approved visual judgement cannot contain a blocking annotation.",
    );
  }
  const evidenceRefs = [...new Set(input.evidenceRefs.filter(Boolean))];
  if (evidenceRefs.length === 0) {
    throw new Error("Visual judgement requires evidence refs.");
  }
  const previewRefs = new Map(
    (workspace.bundle.visualPreviewManifest?.items ?? []).map((item) => [
      item.surfaceId,
      item.artifactRef,
    ]),
  );
  for (const surfaceId of surfaceIds) {
    const previewRef = previewRefs.get(surfaceId);
    if (!previewRef) {
      throw new Error(`Visual judgement has no rendered preview: ${surfaceId}`);
    }
    if (!evidenceRefs.includes(previewRef)) {
      throw new Error(
        `Visual judgement must retain the preview artifact ref: ${surfaceId}`,
      );
    }
  }
  const updatedAt = timestamp(input.updatedAt);
  const judgementId =
    input.judgementId ??
    `visual-${input.judge}-${stableId(`${surfaceIds.join("|")}:${updatedAt}`)}`;
  return updateWorkspace(workspace, updatedAt, (draft) => {
    draft.visualJudgements[judgementId] = {
      schemaVersion: OPENPENCIL_VISUAL_JUDGEMENT_SCHEMA,
      judgementId,
      judge: input.judge,
      judgeRole: "bounded-visual-semantic-judge",
      model: input.model,
      status: input.status,
      confidence: input.confidence,
      surfaceIds,
      annotations,
      evidenceRefs,
      reason: input.reason,
      updatedAt,
      mayWriteApplicationJson: false,
    };
    const revisionDiff = draft.revisionVisualDiff;
    if (
      revisionDiff?.status === "pending" &&
      revisionDiff.changedSurfaceIds.every((surfaceId) =>
        surfaceIds.includes(surfaceId),
      )
    ) {
      if (input.status === "approved") {
        revisionDiff.status = "approved";
        revisionDiff.disposition =
          input.judge === "gemma" ? "gemma-approved" : "human-approved";
        revisionDiff.judgementId = judgementId;
        revisionDiff.blockers = [];
      } else if (
        revisionDiff.changedSurfaceIds.some((surfaceId) =>
          surfaceIds.includes(surfaceId),
        )
      ) {
        revisionDiff.status = "blocked";
        revisionDiff.blockers = [
          input.reason ??
            "The visual judge did not approve the revision pixel delta.",
        ];
        revisionDiff.judgementId = judgementId;
      }
    }
  });
}

export function buildOpenPencilSourceAssemblyManifest(
  workspace: OpenPencilSourceWorkspace,
  input: {
    manifestId?: string;
    evidenceRefs?: string[];
    createdAt?: string;
  } = {},
): OpenPencilSourceWorkspace {
  const createdAt = timestamp(input.createdAt);
  return updateWorkspace(
    workspace,
    createdAt,
    (draft) => {
      const selectedPageIds = Object.values(draft.pageDecisions)
        .filter((decision) => decision.status === "accepted")
        .map((decision) => decision.pageId)
        .sort();
      const selectedSectionIds = Object.values(draft.sectionDecisions)
        .filter((decision) => decision.status === "accepted")
        .map((decision) => decision.sectionId)
        .sort();
      const selectedSurfaceIds = acceptedScreenSurfaceIds(draft);
      const selectedTokenIds = Object.values(draft.tokenDecisions)
        .filter((decision) => decision.status === "accepted")
        .map((decision) => decision.tokenId)
        .sort();
      const surfaceFacts = new Map(
        (draft.bundle.sourceFacts?.surfaceCandidates ?? []).map((surface) => [
          surface.id,
          surface,
        ]),
      );
      const blockers: string[] = [];
      if (
        draft.bundle.generationAdmission.reasonCodes.includes(
          "source_mapping_pending",
        )
      ) {
        blockers.push("Source mapping decisions are incomplete.");
      }
      if (
        draft.bundle.generationAdmission.reasonCodes.includes(
          "token_decisions_pending",
        )
      ) {
        blockers.push("Design token decisions are incomplete.");
      }
      if (!visualJudgementComplete(draft)) {
        blockers.push(
          "Approved bounded visual judgement does not cover every selected screen.",
        );
      }
      const stories = new Map<string, SourceAssemblyStoryMap>();
      for (const surfaceId of selectedSurfaceIds) {
        const decision = draft.surfaceDecisions[surfaceId];
        const fact = surfaceFacts.get(surfaceId);
        const pageId = decision.targetPageId ?? fact?.pageId;
        if (!pageId || !selectedPageIds.includes(pageId)) {
          blockers.push(
            `Selected surface has no accepted target page: ${surfaceId}`,
          );
          continue;
        }
        const storyId =
          decision.storyId ?? fact?.sectionId ?? `story:${pageId}`;
        const story = stories.get(storyId) ?? {
          storyId,
          pageId,
          surfaceIds: [],
        };
        story.surfaceIds.push(surfaceId);
        stories.set(storyId, story);
      }
      const approved = approvedVisualJudgements(draft);
      const visualJudgementIds = approved
        .filter((judgement) =>
          judgement.surfaceIds.some((surfaceId) =>
            selectedSurfaceIds.includes(surfaceId),
          ),
        )
        .map((judgement) => judgement.judgementId)
        .sort();
      const evidenceRefs = [
        ...(input.evidenceRefs ?? []),
        ...approved.flatMap((judgement) => judgement.evidenceRefs),
      ].filter(
        (value, index, values) => value && values.indexOf(value) === index,
      );
      const manifestId =
        input.manifestId ??
        `assembly-${stableId(`${draft.sourceId}:${draft.revision}:${selectedSurfaceIds.join("|")}`)}`;
      draft.assemblyManifest = {
        schemaVersion: "cogo.studio.openpencil-source-assembly-manifest.v1",
        manifestId,
        sourceId: draft.sourceId,
        sourceRevision: draft.revision,
        status: blockers.length === 0 ? "validated" : "blocked",
        selectedPageIds,
        selectedSectionIds,
        selectedSurfaceIds,
        selectedTokenIds,
        storyMap: [...stories.values()].map((story) => ({
          ...story,
          surfaceIds: [...story.surfaceIds].sort(),
        })),
        visualJudgementIds,
        blockers: [...new Set(blockers)],
        evidenceRefs,
        createdAt,
        mayWriteApplicationJson: false,
      };
      draft.candidateBinding = null;
      draft.runtimeProof = null;
    },
    false,
  );
}

export function recordOpenPencilSourceCandidateBinding(
  workspace: OpenPencilSourceWorkspace,
  input: {
    candidateId: string;
    manifestId: string;
    sourceRevision: number;
    inputSha256: string;
    applicationArtifactRef: string;
    previewUrl: string;
    createdAt?: string;
  },
): OpenPencilSourceWorkspace {
  const manifest = workspace.assemblyManifest;
  if (!manifest || manifest.status !== "validated") {
    throw new Error("Source candidate binding requires a validated assembly.");
  }
  if (workspace.bundle.generationAdmission.status !== "admitted") {
    throw new Error("Source candidate binding requires generation admission.");
  }
  if (
    input.manifestId !== manifest.manifestId ||
    input.sourceRevision !== manifest.sourceRevision
  ) {
    throw new Error(
      "Source candidate binding does not match the active assembly.",
    );
  }
  const createdAt = timestamp(input.createdAt);
  const draft = structuredClone(workspace);
  draft.updatedAt = createdAt;
  draft.candidateBinding = {
    schemaVersion: "cogo.studio.openpencil-source-candidate-binding.v1",
    candidateId: input.candidateId,
    sourceId: workspace.sourceId,
    sourceRevision: input.sourceRevision,
    manifestId: input.manifestId,
    inputSha256: input.inputSha256,
    applicationArtifactRef: input.applicationArtifactRef,
    previewUrl: input.previewUrl,
    authorityMode: "client-validated-candidate",
    createdAt,
  };
  draft.runtimeProof = null;
  draft.bundle = withProgressAdmission(draft);
  return OpenPencilSourceWorkspaceSchema.parse(draft);
}

export function recordVerifiedOpenPencilSourceRuntimeProof(
  workspace: OpenPencilSourceWorkspace,
  input: OpenPencilRuntimeProofResult,
): OpenPencilSourceWorkspace {
  const verified = OpenPencilRuntimeProofResultSchema.parse(input);
  const createdAt = timestamp(verified.createdAt);
  return updateWorkspace(
    workspace,
    createdAt,
    (draft) => {
      const manifest = draft.assemblyManifest;
      const candidate = draft.candidateBinding;
      if (!manifest)
        throw new Error("Runtime proof requires an assembly manifest.");
      if (!candidate) {
        throw new Error("Runtime proof requires a bound source candidate.");
      }
      if (verified.sourceId !== draft.sourceId) {
        throw new Error(
          "Runtime proof source does not match the active source.",
        );
      }
      if (verified.manifestId !== manifest.manifestId) {
        throw new Error(
          "Runtime proof manifest does not match the active assembly.",
        );
      }
      if (
        verified.manifestId !== candidate.manifestId ||
        verified.previewUrl !== candidate.previewUrl ||
        verified.applicationArtifactRef !== candidate.applicationArtifactRef
      ) {
        throw new Error(
          "Runtime proof does not match the bound source candidate.",
        );
      }
      if (
        !verified.runner.startsWith("cogo-openpencil-playwright-proof-runner/")
      ) {
        throw new Error(
          "Runtime proof was not produced by the bounded proof runner.",
        );
      }
      if (
        verified.scenarios.some((scenario) =>
          scenario.evidenceRefs.some(
            (evidenceRef) => !verified.evidenceRefs.includes(evidenceRef),
          ),
        )
      ) {
        throw new Error(
          "Runtime scenario evidence is not retained by the proof result.",
        );
      }
      const blockers = [
        ...(manifest.status !== "validated"
          ? ["Assembly manifest is not validated."]
          : []),
        ...verified.scenarios
          .filter((scenario) => scenario.status !== "passed")
          .map((scenario) => `Runtime scenario failed: ${scenario.scenarioId}`),
        ...verified.blockers,
      ];
      draft.runtimeProof = {
        ...verified,
        status: blockers.length === 0 ? "passed" : "failed",
        blockers: [...new Set(blockers)],
        createdAt,
      };
    },
    false,
  );
}

export function summarizeOpenPencilSourceWorkspace(
  workspace: OpenPencilSourceWorkspace | null,
) {
  const counts = (values: Array<{ status: SourceDecisionStatus }>) => ({
    total: values.length,
    pending: values.filter((value) => value.status === "pending").length,
    accepted: values.filter((value) => value.status === "accepted").length,
    rejected: values.filter((value) => value.status === "rejected").length,
  });
  const releaseAdmission = workspace
    ? evaluateOpenPencilSourceReleaseAdmission(workspace)
    : {
        status: "blocked" as const,
        reasonCodes: ["source_workspace_missing", "runtime_proof_pending"],
        requiredGateIds: ["source-workspace-loaded", "runtime-proof-passed"],
      };
  return {
    sourceId: workspace?.sourceId ?? null,
    sourceRevision: workspace?.bundle.revisionLineage?.sourceRevision ?? 0,
    parentSourceId: workspace?.bundle.revisionLineage?.parentSourceId ?? null,
    revision: workspace?.revision ?? 0,
    pages: counts(Object.values(workspace?.pageDecisions ?? {})),
    sections: counts(Object.values(workspace?.sectionDecisions ?? {})),
    surfaces: counts(Object.values(workspace?.surfaceDecisions ?? {})),
    tokens: counts(Object.values(workspace?.tokenDecisions ?? {})),
    visualJudgements: Object.keys(workspace?.visualJudgements ?? {}).length,
    revisionVisualDiffStatus:
      workspace?.revisionVisualDiff?.status ?? "not-required",
    revisionVisualDiffDisposition:
      workspace?.revisionVisualDiff?.disposition ?? "not-required",
    revisionChangedSurfaceCount:
      workspace?.revisionVisualDiff?.changedSurfaceIds.length ?? 0,
    assemblyStatus: workspace?.assemblyManifest?.status ?? "missing",
    candidateStatus: workspace?.candidateBinding ? "bound" : "missing",
    candidateId: workspace?.candidateBinding?.candidateId ?? null,
    runtimeProofStatus: workspace?.runtimeProof?.status ?? "missing",
    sourceRightsStatus:
      workspace?.sourceRightsDeclaration?.review.status ?? "not-recorded",
    sourceRightsEvidenceRef:
      workspace?.sourceRightsDeclaration?.evidenceRef.uri ?? null,
    generationAdmission:
      workspace?.bundle.generationAdmission.status ?? "blocked",
    requiredGateIds:
      workspace?.bundle.generationAdmission.requiredGateIds ?? [],
    releaseAdmission: releaseAdmission.status,
    releaseReasonCodes: releaseAdmission.reasonCodes,
    requiredReleaseGateIds: releaseAdmission.requiredGateIds,
  };
}
